import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import Box from "../../../setupPage/SharedComponents/Box";

const STATUS_OPTIONS = ["New", "Contacted", "Pending", "Resolved", "Closed"];
const PAGE_SIZE = 2147483647;

const getStatusOptionsForRow = (currentStatus) => {
  const options = [...STATUS_OPTIONS];
  if (currentStatus && !options.includes(currentStatus)) {
    options.unshift(currentStatus);
  }
  return options;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeInquiriesResponse = (data) => {
  if (Array.isArray(data)) {
    return { items: data, totalCount: data[0]?.totalCount ?? data.length };
  }
  if (Array.isArray(data?.items)) {
    return {
      items: data.items,
      totalCount: data.totalCount ?? data.items.length,
    };
  }
  if (Array.isArray(data?.data)) {
    return {
      items: data.data,
      totalCount: data.totalCount ?? data.data.length,
    };
  }
  return { items: [], totalCount: 0 };
};

const formatDateTime = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const Inquiry = () => {
  const backendUrl = process.env.REACT_APP_API_URL;
  const [inquiries, setInquiries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [statusDraft, setStatusDraft] = useState({});
  const [remarksDraft, setRemarksDraft] = useState({});

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${backendUrl}Inquiry/GetInquiries`, {
        params: { page: 1, pageSize: PAGE_SIZE },
        headers: { accept: "*/*", ...getAuthHeaders() },
      });
      const { items, totalCount: total } = normalizeInquiriesResponse(res.data);
      setInquiries(items);
      setTotalCount(total);
      const statusMap = {};
      const remarksMap = {};
      items.forEach((item) => {
        statusMap[item.id] = item.status || "New";
        remarksMap[item.id] = item.remarks ?? "";
      });
      setStatusDraft(statusMap);
      setRemarksDraft(remarksMap);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load inquiries.";
      toast.error(message);
      setInquiries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleStatusChange = (id, status) => {
    setStatusDraft((prev) => ({ ...prev, [id]: status }));
  };

  const handleRemarksChange = (id, remarks) => {
    setRemarksDraft((prev) => ({ ...prev, [id]: remarks }));
  };

  const hasRowChanges = (inquiry) => {
    const newStatus = statusDraft[inquiry.id] ?? inquiry.status ?? "New";
    const newRemarks = remarksDraft[inquiry.id] ?? inquiry.remarks ?? "";
    const statusChanged = newStatus !== (inquiry.status || "New");
    const remarksChanged = newRemarks !== (inquiry.remarks ?? "");
    return statusChanged || remarksChanged;
  };

  const handleUpdateInquiry = async (inquiry) => {
    if (!hasRowChanges(inquiry)) {
      toast.info("No changes to save.");
      return;
    }

    const newStatus = statusDraft[inquiry.id] ?? inquiry.status ?? "New";
    const newRemarks = remarksDraft[inquiry.id] ?? inquiry.remarks ?? "";

    setUpdatingId(inquiry.id);
    try {
      const payload = {
        id: inquiry.id,
        name: inquiry.name || "",
        email: inquiry.email || "",
        mobile: inquiry.mobile || "",
        remarks: newRemarks,
        status: newStatus,
        totalCount: inquiry.totalCount ?? 0,
        createdAt: inquiry.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await axios.put(
        `${backendUrl}Inquiry/UpdateInquiry/${inquiry.id}`,
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      toast.success("Inquiry updated.");
      await fetchInquiries();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update inquiry.";
      toast.error(message);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <SetupContainer>
      <SetupTittle text="Inquiry" />

      <Box>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <p className="text-gray-600">
            Manage customer inquiries from the landing page. Update status and remarks.
          </p>
          <button
            type="button"
            onClick={fetchInquiries}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-[#6D4C41] text-white font-medium hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="w-full overflow-x-auto bg-[#fceee5] rounded-lg">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-[#f5cfb5] text-lg text-center">
                <th className="py-4 px-3">S/N</th>
                <th className="py-4 px-3">Name</th>
                <th className="py-4 px-3">Email</th>
                <th className="py-4 px-3">Mobile</th>
                <th className="py-4 px-3">Remarks</th>
                <th className="py-4 px-3">Status</th>
                <th className="py-4 px-3">Created</th>
                <th className="py-4 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && inquiries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 font-medium">
                    Loading inquiries...
                  </td>
                </tr>
              ) : inquiries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 font-medium">
                    No inquiries found.
                  </td>
                </tr>
              ) : (
                inquiries.map((item, index) => (
                  <tr
                    key={item.id}
                    className={
                      index % 2 !== 0 ? "bg-[#fce8db]" : "bg-[#fcf4ef]"
                    }
                  >
                    <td className="text-center py-3 font-semibold">
                      {index + 1}
                    </td>
                    <td className="text-center py-3 px-3 font-semibold">
                      {item.name || "—"}
                    </td>
                    <td className="text-center py-3 px-3 font-semibold break-all">
                      {item.email || "—"}
                    </td>
                    <td className="text-center py-3 px-3 font-semibold">
                      {item.mobile || "—"}
                    </td>
                    <td className="py-3 px-3 min-w-[220px]">
                      <textarea
                        value={remarksDraft[item.id] ?? item.remarks ?? ""}
                        onChange={(e) =>
                          handleRemarksChange(item.id, e.target.value)
                        }
                        disabled={updatingId === item.id}
                        rows={3}
                        placeholder="Add or edit remarks..."
                        className="w-full min-w-[200px] border border-gray-300 rounded-md px-3 py-2 bg-white text-sm font-medium resize-y"
                      />
                    </td>
                    <td className="text-center py-3 px-3">
                      <select
                        value={statusDraft[item.id] ?? item.status ?? "New"}
                        onChange={(e) =>
                          handleStatusChange(item.id, e.target.value)
                        }
                        disabled={updatingId === item.id}
                        className="border border-gray-300 rounded-md px-3 py-2 bg-white text-sm font-medium min-w-[140px]"
                      >
                        {getStatusOptionsForRow(item.status).map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="text-center py-3 px-3 text-sm font-semibold whitespace-nowrap">
                      {formatDateTime(item.createdAt)}
                    </td>
                    <td className="text-center py-3 px-3">
                      <button
                        type="button"
                        onClick={() => handleUpdateInquiry(item)}
                        disabled={
                          updatingId === item.id || !hasRowChanges(item)
                        }
                        className="px-3 py-1.5 rounded-md bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingId === item.id ? "Saving..." : "Save"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <p className="text-sm text-gray-600 mt-6">
            {totalCount} {totalCount === 1 ? "inquiry" : "inquiries"} total
          </p>
        )}
      </Box>
    </SetupContainer>
  );
};

export default Inquiry;
